<script setup lang="ts">
// グラデーションエディタ: バーの上のストップをドラッグ、クリックで追加、選択して色 / 不透明度 / 位置を編集、× で削除。
import { computed, ref, watch } from 'vue'
import { useI18n } from '@/i18n'

export interface GradientStop {
  pos: number
  hex: string
  alpha?: number
}
const props = defineProps<{ stops: GradientStop[]; withAlpha?: boolean }>()
const emit = defineEmits<{ input: [stops: GradientStop[]]; change: [stops: GradientStop[]] }>()
const { t } = useI18n()
const local = ref<GradientStop[]>(props.stops.map(s => ({ ...s })))
const selected = ref(0)
const dragging = ref<number | null>(null)
watch(() => props.stops, s => { if (dragging.value === null) local.value = s.map(x => ({ ...x })) }, { deep: true })
const sorted = computed(() => [...local.value].map((s, i) => ({ ...s, i })).sort((a, b) => a.pos - b.pos))
const css = computed(() => {
  const parts = sorted.value.map(s => {
    const a = props.withAlpha ? (s.alpha ?? 1) : 1
    const r = parseInt(s.hex.slice(1, 3), 16), g = parseInt(s.hex.slice(3, 5), 16), b = parseInt(s.hex.slice(5, 7), 16)
    return `rgba(${r},${g},${b},${a}) ${Math.round(s.pos * 100)}%`
  })
  return `linear-gradient(90deg, ${parts.join(', ')})`
})
const bar = ref<HTMLDivElement | null>(null)
function posOf(e: PointerEvent | MouseEvent): number {
  const r = bar.value!.getBoundingClientRect()
  return Math.max(0, Math.min(1, (e.clientX - r.left) / r.width))
}
function onBarDown(e: PointerEvent) {
  // 既存ストップの近くなら選択 + ドラッグ、そうでなければ追加
  const p = posOf(e)
  const r = bar.value!.getBoundingClientRect()
  let hit = -1
  local.value.forEach((s, i) => { if (Math.abs((s.pos - p) * r.width) <= 7) hit = i })
  if (hit < 0) {
    const color = sampleColor(p)
    local.value.push({ pos: Math.round(p * 1000) / 1000, hex: color, ...(props.withAlpha ? { alpha: 1 } : {}) })
    hit = local.value.length - 1
    emit('input', local.value)
  }
  selected.value = hit
  dragging.value = hit
  ;(e.currentTarget as Element).setPointerCapture(e.pointerId)
}
function onBarMove(e: PointerEvent) {
  if (dragging.value === null) return
  const s = local.value[dragging.value]
  if (!s) return
  s.pos = Math.round(posOf(e) * 1000) / 1000
  emit('input', local.value)
}
function onBarUp() {
  if (dragging.value === null) return
  dragging.value = null
  emit('change', local.value)
}
function sampleColor(p: number): string {
  const s = sorted.value
  if (!s.length) return '#000000'
  if (p <= s[0]!.pos) return s[0]!.hex
  for (let i = 0; i < s.length - 1; i++) {
    const a = s[i]!, b = s[i + 1]!
    if (p >= a.pos && p <= b.pos) {
      const t = b.pos === a.pos ? 0 : (p - a.pos) / (b.pos - a.pos)
      const mix = (k: number) => Math.round(parseInt(a.hex.slice(k, k + 2), 16) * (1 - t) + parseInt(b.hex.slice(k, k + 2), 16) * t)
      return '#' + [1, 3, 5].map(k => mix(k).toString(16).padStart(2, '0')).join('')
    }
  }
  return s[s.length - 1]!.hex
}
function update(p: Partial<GradientStop>, commit: boolean) {
  const s = local.value[selected.value]
  if (!s) return
  Object.assign(s, p)
  if (commit) emit('change', local.value)
  else emit('input', local.value)
}
function remove() {
  if (local.value.length <= 2) return
  local.value.splice(selected.value, 1)
  selected.value = Math.max(0, selected.value - 1)
  emit('change', local.value)
}
function reverse() {
  for (const s of local.value) s.pos = Math.round((1 - s.pos) * 1000) / 1000
  emit('change', local.value)
}
const cur = computed(() => local.value[selected.value])
</script>

<template>
  <div class="ge">
    <div ref="bar" class="bar" :style="{ background: css }" @pointerdown="onBarDown" @pointermove="onBarMove" @pointerup="onBarUp" @pointercancel="onBarUp">
      <span v-for="(s, i) in local" :key="i" class="stop" :class="{ sel: i === selected }" :style="{ left: s.pos * 100 + '%', background: s.hex }" />
    </div>
    <div v-if="cur" class="row ctl">
      <input type="color" :value="cur.hex" :title="t('gradient.color')" @input="update({ hex: ($event.target as HTMLInputElement).value }, false)" @change="update({ hex: ($event.target as HTMLInputElement).value }, true)" />
      <label class="row"><span class="muted">{{ t('gradient.position') }}</span><input class="num mono" type="number" min="0" max="100" :value="Math.round(cur.pos * 100)" @change="update({ pos: Math.max(0, Math.min(1, Number(($event.target as HTMLInputElement).value) / 100)) }, true)" />%</label>
      <label v-if="withAlpha" class="row"><span class="muted">{{ t('gradient.opacity') }}</span><input class="num mono" type="number" min="0" max="100" :value="Math.round((cur.alpha ?? 1) * 100)" @change="update({ alpha: Math.max(0, Math.min(1, Number(($event.target as HTMLInputElement).value) / 100)) }, true)" />%</label>
      <button class="ghost tiny" :title="t('gradient.reverse')" @click="reverse">⇄</button>
      <button class="ghost tiny" :title="t('gradient.remove')" :disabled="local.length <= 2" @click="remove">×</button>
    </div>
  </div>
</template>

<style scoped>
.ge {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 200px;
}
.bar {
  position: relative;
  height: 18px;
  border: 1px solid var(--line-strong);
  border-radius: 3px;
  cursor: copy;
  touch-action: none;
  background-clip: padding-box;
  margin: 0 6px 8px;
}
.stop {
  position: absolute;
  top: 100%;
  width: 10px;
  height: 10px;
  margin-left: -6px;
  margin-top: -3px;
  border: 1px solid var(--fg-0);
  border-radius: 2px;
  box-shadow: 0 0 0 1px #fff;
  cursor: ew-resize;
}
.stop.sel {
  outline: 2px solid var(--accent);
}
.ctl {
  gap: 6px;
  flex-wrap: wrap;
  font-size: 11px;
}
.num {
  width: 44px;
}
.tiny {
  width: 22px;
  height: 20px;
  padding: 0;
}
</style>
